import React, { useEffect, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  Checkbox,
  ListItemText,
} from '@mui/material'
import { useForm, Controller } from 'react-hook-form'
import { useAppSelector, useAppDispatch } from '@hooks/redux'
import {
  fetchDashboard,
  createDashboard,
  updateDashboard,
  deleteDashboard,
} from '@store/slices/dashboardsSlice'
import { fetchApiariesAndGroups } from '@store/slices/locationsSlice'
import { Hive } from '@types'
import SaveToolbar from '@components/common/SaveToolbar'

interface DashboardFormData {
  name: string
  description: string
  code: string
  hive_ids: number[]
  interval: number
  show_inspections: boolean
}

/**
 * DashboardEdit Page Component
 * 
 * Create or edit public dashboards for sharing hive data.
 * Features:
 * - Configure dashboard name and description
 * - Select hives to display
 * - Set data refresh interval
 * - Generate unique dashboard code
 * 
 * @component
 */
const DashboardEdit: React.FC = () => {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const dispatch = useAppDispatch()

  const isCreateMode = !id
  const { selectedDashboard, loadingState, error } = useAppSelector((state) => state.dashboards)
  const { hives } = useAppSelector((state) => state.locations)

  const [showLoadingIcon, setShowLoadingIcon] = useState(false)

  const { control, handleSubmit, watch, setValue, formState: { errors } } = useForm<DashboardFormData>({
    defaultValues: {
      name: '',
      description: '',
      code: '',
      hive_ids: [],
      interval: 300,
      show_inspections: true,
    },
  })

  useEffect(() => {
    dispatch(fetchApiariesAndGroups())
  }, [dispatch])

  useEffect(() => {
    if (id && !isCreateMode) {
      dispatch(fetchDashboard(parseInt(id)))
    }
  }, [dispatch, id, isCreateMode])

  useEffect(() => {
    if (selectedDashboard && !isCreateMode) {
      setValue('name', selectedDashboard.name)
      setValue('description', selectedDashboard.description || '')
      setValue('code', selectedDashboard.code)
      setValue('hive_ids', selectedDashboard.hive_ids || [])
      setValue('interval', selectedDashboard.interval || 300)
      setValue('show_inspections', selectedDashboard.show_inspections ?? true)
    }
  }, [selectedDashboard, isCreateMode, setValue])

  useEffect(() => {
    if (isCreateMode) {
      // Generate random code for new dashboard
      const randomCode = Math.random().toString(36).substring(2, 10).toUpperCase()
      setValue('code', randomCode)
    }
  }, [isCreateMode, setValue])

  const onSubmit = async (data: DashboardFormData) => {
    setShowLoadingIcon(true)
    try {
      if (isCreateMode) {
        await dispatch(createDashboard(data))
      } else {
        await dispatch(updateDashboard({ id: parseInt(id!), data }))
      }
      navigate('/dashboards')
    } catch (err) {
      console.error('Failed to save dashboard:', err)
    } finally {
      setShowLoadingIcon(false)
    }
  }

  const handleDelete = async () => {
    if (selectedDashboard && window.confirm(`Are you sure you want to delete dashboard "${selectedDashboard.name}"?`)) {
      setShowLoadingIcon(true)
      await dispatch(deleteDashboard(selectedDashboard.id))
      navigate('/dashboards')
    }
  }

  const getSelectedHives = (hiveIds: number[]): Hive[] => {
    return hives.filter((hive) => hiveIds.includes(hive.id))
  }

  if (loadingState === 'pending' && !isCreateMode) {
    return (
      <Container>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
          <CircularProgress size={50} />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="lg" sx={{ py: 2, px: { xs: 2, sm: 3 } }}>
      <SaveToolbar
        onSave={handleSubmit(onSubmit)}
        onDelete={!isCreateMode ? handleDelete : undefined}
        saveDisabled={showLoadingIcon}
        loading={showLoadingIcon}
        showDelete={!isCreateMode}
      />

      <Typography variant="h4" component="h1" gutterBottom sx={{ mt: 2 }}>
        {isCreateMode ? 'New Dashboard' : 'Edit Dashboard'}
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Dashboard Form */}
      <Card>
        <CardContent>
          <Typography variant="overline" display="block" gutterBottom>
            Dashboard Settings
          </Typography>

          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              {/* Name */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="name"
                  control={control}
                  rules={{ required: 'Dashboard name is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Dashboard Name"
                      error={!!errors.name}
                      helperText={errors.name?.message}
                    />
                  )}
                />
              </Grid>

              {/* Code */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="code"
                  control={control}
                  rules={{ required: 'Dashboard code is required' }}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Dashboard Code"
                      error={!!errors.code}
                      helperText={errors.code?.message || 'Unique code for sharing'}
                      InputProps={{
                        readOnly: !isCreateMode,
                      }}
                    />
                  )}
                />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      label="Description"
                      multiline
                      rows={3}
                    />
                  )}
                />
              </Grid>

              {/* Hive Selection */}
              <Grid item xs={12}>
                <Controller
                  name="hive_ids"
                  control={control}
                  rules={{ required: 'At least one hive must be selected' }}
                  render={({ field }) => (
                    <FormControl fullWidth error={!!errors.hive_ids}>
                      <InputLabel>Select Hives</InputLabel>
                      <Select
                        {...field}
                        multiple
                        input={<OutlinedInput label="Select Hives" />}
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {getSelectedHives(selected as number[]).map((hive) => (
                              <Chip key={hive.id} label={hive.name} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {hives.map((hive) => (
                          <MenuItem key={hive.id} value={hive.id}>
                            <Checkbox checked={field.value.includes(hive.id)} />
                            <ListItemText primary={hive.name} secondary={hive.location?.name} />
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.hive_ids && (
                        <Typography variant="caption" color="error">
                          {errors.hive_ids.message}
                        </Typography>
                      )}
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Refresh Interval */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="interval"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Refresh Interval</InputLabel>
                      <Select {...field} label="Refresh Interval">
                        <MenuItem value={60}>1 minute</MenuItem>
                        <MenuItem value={300}>5 minutes</MenuItem>
                        <MenuItem value={600}>10 minutes</MenuItem>
                        <MenuItem value={1800}>30 minutes</MenuItem>
                        <MenuItem value={3600}>1 hour</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>

              {/* Show Inspections */}
              <Grid item xs={12} md={6}>
                <Controller
                  name="show_inspections"
                  control={control}
                  render={({ field }) => (
                    <FormControl fullWidth>
                      <InputLabel>Show Inspections</InputLabel>
                      <Select {...field} label="Show Inspections">
                        <MenuItem value={true as any}>Yes</MenuItem>
                        <MenuItem value={false as any}>No</MenuItem>
                      </Select>
                    </FormControl>
                  )}
                />
              </Grid>
            </Grid>
          </form>
        </CardContent>
      </Card>

      {/* Preview Info */}
      {!isCreateMode && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            Dashboard URL: <strong>{`${window.location.origin}/dashboard/${watch('code')}`}</strong>
          </Typography>
        </Alert>
      )}
    </Container>
  )
}

export default DashboardEdit
